import React, { useState } from "react";
import {
  DEFAULT_CAMPUS_OPTIONS,
  DEFAULT_EMAIL_SETTINGS,
  DEFAULT_FORM_FIELDS,
  CHOICE_FORM_TYPES,
  FORM_TYPES,
  ID_LABEL,
  ensureAllSheets,
  ensureSpreadsheetChecked,
  findIdFieldIndex,
  writeCampusOptions,
  writeEmailSettings,
  writeFormFields,
} from "../api/sheets";
import { createDetachedResponsesSheet, ensureFormOnStart, linkExistingFormById, setThemedTemplateFromPicked } from "../api/forms";
import { pickGoogleForm } from "../auth/googlePicker";
import PickerCookieHint from "./PickerCookieHint";
import { EmailSettings, FormField } from "../types/sheets";
import { useT } from "../i18n";
import { setFieldworkerMode, setSharedProject } from "../utils/fieldworkerMode";
import EmailBackendInstructions from "./EmailBackendInstructions";

interface SetupBanner {
  url?: string;
  error?: string;
}

interface Props {
  onComplete: (banner?: SetupBanner) => void;
}

type Step = 1 | 2 | 3 | 4 | "loading" | "done";

interface BackendIds {
  dataSpreadsheetId: string;
  formId: string;
  responsesSheetId: string;
}

// ─── helpers ───────────────────────────────────────────────────────────────

function cloneFields(fields: readonly FormField[]): FormField[] {
  return fields.map((f) => ({ ...f, values: [...f.values] }));
}

// ─── sub-components ────────────────────────────────────────────────────────

function StepIndicator({ step, t }: { step: Step; t: ReturnType<typeof useT> }) {
  const labels = [t("wizard.stepWelcome"), t("wizard.stepEmail"), t("wizard.stepCampus"), t("wizard.stepForm")];
  const current = typeof step === "number" ? step : 4;
  return (
    <div className="flex items-center gap-2 justify-center mb-6">
      {labels.map((label, i) => {
        const num = i + 1;
        const active = num === current;
        const done = num < current;
        return (
          <React.Fragment key={num}>
            {i > 0 && (
              <div className={`h-px flex-1 max-w-12 ${done ? "bg-pink-400" : "bg-gray-200"}`} />
            )}
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  done
                    ? "bg-pink-500 text-white"
                    : active
                    ? "bg-pink-600 text-white ring-2 ring-pink-300"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                {done ? <i className="fa-solid fa-check text-xs" /> : num}
              </div>
              <span className={`text-xs hidden sm:block ${active ? "text-pink-700 font-medium" : "text-gray-400"}`}>
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Step 1: Welcome ───────────────────────────────────────────────────────

function WelcomeStep({ t }: { t: ReturnType<typeof useT> }) {
  const items = [
    { icon: "fa-table", text: t("wizard.welcomeSheet") },
    { icon: "fa-folder", text: t("wizard.welcomeFolders") },
    { icon: "fa-clipboard-list", text: t("wizard.welcomeForm") },
    { icon: "fa-code", text: t("wizard.welcomeBackend") },
  ];
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{t("wizard.welcomeTitle")}</h2>
        <p className="mt-2 text-gray-600">{t("wizard.welcomeBodyEmail")}</p>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
          {t("wizard.welcomeCreates")}
        </p>
        <ul className="space-y-2">
          {items.map(({ icon, text }) => (
            <li key={text} className="flex items-start gap-3">
              <span className="mt-0.5 w-6 text-center text-pink-500">
                <i className={`fa-solid ${icon}`} aria-hidden="true" />
              </span>
              <span className="text-gray-700">{text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Step 2: Email ─────────────────────────────────────────────────────────

function EmailStep({
  email,
  onChange,
  t,
}: {
  email: EmailSettings;
  onChange: (k: keyof EmailSettings, v: string) => void;
  t: ReturnType<typeof useT>;
}) {
  const fields: { key: keyof EmailSettings; label: string; multiline?: boolean }[] = [
    { key: "dest", label: t("email.dest") },
    { key: "cc", label: t("email.cc") },
    { key: "bcc", label: t("email.bcc") },
    { key: "sendername", label: t("email.senderName") },
    { key: "subject", label: t("email.subject") },
    { key: "topmessage", label: t("email.topMessage"), multiline: true },
    { key: "bottommessage", label: t("email.bottomMessage"), multiline: true },
  ];
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900">{t("wizard.emailTitle")}</h2>
        <p className="mt-1 text-sm text-gray-600">{t("wizard.emailIntro")}</p>
      </div>
      <div className="space-y-3">
        {fields.map(({ key, label, multiline }) => (
          <label key={key} className="block">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            {multiline ? (
              <textarea
                value={email[key]}
                onChange={(e) => onChange(key, e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-pink-400 focus:ring-1 focus:ring-pink-300 outline-none resize-y"
              />
            ) : (
              <input
                type="text"
                value={email[key]}
                onChange={(e) => onChange(key, e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-pink-400 focus:ring-1 focus:ring-pink-300 outline-none"
              />
            )}
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Step 3: Campus Options ────────────────────────────────────────────────

interface CampusRow {
  label: string;
  value: string;
}

function CampusStep({
  rows,
  onChange,
  t,
}: {
  rows: CampusRow[];
  onChange: (rows: CampusRow[]) => void;
  t: ReturnType<typeof useT>;
}) {
  function update(i: number, field: keyof CampusRow, val: string) {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r));
    onChange(next);
  }
  function remove(i: number) {
    onChange(rows.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...rows, { label: "", value: "" }]);
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900">{t("wizard.campusTitle")}</h2>
        <p className="mt-1 text-sm text-gray-600">{t("wizard.campusIntro")}</p>
      </div>
      <div className="space-y-2">
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide px-1">
          <span>{t("wizard.campusLabel")}</span>
          <span>{t("wizard.campusValue")}</span>
          <span />
        </div>
        {rows.map((row, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
            <input
              type="text"
              value={row.label}
              placeholder={t("wizard.campusLabel")}
              onChange={(e) => update(i, "label", e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-pink-400 focus:ring-1 focus:ring-pink-300 outline-none"
            />
            <input
              type="text"
              value={row.value}
              placeholder={t("wizard.campusValue")}
              onChange={(e) => update(i, "value", e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-pink-400 focus:ring-1 focus:ring-pink-300 outline-none"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors"
              title={t("wizard.campusRemove")}
              aria-label={t("wizard.campusRemove")}
            >
              <i className="fa-solid fa-trash-can text-sm" aria-hidden="true" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="mt-1 flex items-center gap-2 text-sm text-pink-600 hover:text-pink-700 font-medium"
        >
          <i className="fa-solid fa-plus" aria-hidden="true" />
          {t("wizard.campusAddRow")}
        </button>
      </div>
    </div>
  );
}

// ─── Step 4: Form Builder ──────────────────────────────────────────────────

function FormStep({
  fields,
  onChange,
  t,
}: {
  fields: FormField[];
  onChange: (fields: FormField[]) => void;
  t: ReturnType<typeof useT>;
}) {
  const idIndex = findIdFieldIndex(fields);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  function updateField(i: number, patch: Partial<FormField>) {
    onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  }
  function reorderField(from: number, to: number) {
    // The title card is pinned at index 0 and never moves.
    if (from === to || from === 0 || to === 0) return;
    if (from < 0 || to < 0 || from >= fields.length || to >= fields.length) return;
    const next = [...fields];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  }
  function handleDrop(target: number) {
    if (dragIndex !== null) reorderField(dragIndex, target);
    setDragIndex(null);
    setDropIndex(null);
  }
  function moveUp(i: number) {
    if (i <= 1) return; // first card (title) is pinned at 0; second can't go above title
    const next = [...fields];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  }
  function moveDown(i: number) {
    if (i === 0 || i >= fields.length - 1) return;
    const next = [...fields];
    [next[i], next[i + 1]] = [next[i + 1], next[i]];
    onChange(next);
  }
  function removeField(i: number) {
    onChange(fields.filter((_, idx) => idx !== i));
  }
  function addOption(i: number) {
    const f = fields[i];
    updateField(i, { values: [...f.values, ""] });
  }
  function updateOption(fi: number, oi: number, val: string) {
    const values = fields[fi].values.map((v, idx) => (idx === oi ? val : v));
    updateField(fi, { values });
  }
  function removeOption(fi: number, oi: number) {
    updateField(fi, { values: fields[fi].values.filter((_, idx) => idx !== oi) });
  }
  function addField(type: string) {
    onChange([...fields, { label: "", type, values: [], description: "" }]);
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900">{t("wizard.formTitle")}</h2>
        <p className="mt-1 text-sm text-gray-600">{t("wizard.formIntro")}</p>
      </div>

      <div className="space-y-3">
        {fields.map((field, i) => {
          const isTitle = field.type === "title";
          const isId = i === idIndex;
          const isFirstCard = i === 0;
          const isChoice = CHOICE_FORM_TYPES.includes(field.type);

          return (
            <div
              key={i}
              onDragOver={(e) => {
                if (isFirstCard || dragIndex === null) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (dropIndex !== i) setDropIndex(i);
              }}
              onDrop={(e) => {
                if (isFirstCard || dragIndex === null) return;
                e.preventDefault();
                handleDrop(i);
              }}
              className={`rounded-xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md ${
                isTitle ? "border-pink-200 bg-pink-50/30" : "border-gray-200"
              } ${dragIndex !== null && dropIndex === i && dragIndex !== i ? "ring-2 ring-pink-300" : ""} ${
                dragIndex === i ? "opacity-50" : ""
              }`}
            >
              {/* Card header row */}
              <div className="flex items-start gap-2 mb-3">
                {!isFirstCard && (
                  <div className="flex flex-col items-center gap-0.5 pt-0.5 shrink-0">
                    <span
                      draggable
                      onDragStart={(e) => {
                        setDragIndex(i);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => {
                        setDragIndex(null);
                        setDropIndex(null);
                      }}
                      className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 pb-0.5"
                      title={t("wizard.formDragToReorder")}
                      aria-label={t("wizard.formDragToReorder")}
                    >
                      <i className="fa-solid fa-grip-vertical text-xs" aria-hidden="true" />
                    </span>
                    <button
                      type="button"
                      onClick={() => moveUp(i)}
                      disabled={i <= 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-20"
                      aria-label="Move up"
                    >
                      <i className="fa-solid fa-chevron-up text-xs" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveDown(i)}
                      disabled={i >= fields.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-20"
                      aria-label="Move down"
                    >
                      <i className="fa-solid fa-chevron-down text-xs" aria-hidden="true" />
                    </button>
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  {isTitle ? (
                    <>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => updateField(i, { label: e.target.value })}
                        placeholder={t("formSettings.titlePlaceholder")}
                        className="w-full text-lg font-semibold border-0 border-b-2 border-gray-200 focus:border-pink-400 bg-transparent px-0 py-1 outline-none mb-2"
                      />
                      <textarea
                        value={field.description}
                        onChange={(e) => updateField(i, { description: e.target.value })}
                        placeholder={t("formSettings.formDescriptionPlaceholder")}
                        rows={3}
                        className="w-full text-sm text-gray-600 border-0 border-b border-gray-200 focus:border-pink-300 bg-transparent px-0 py-1 outline-none resize-y"
                      />
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) => updateField(i, { label: e.target.value })}
                          placeholder="Question"
                          disabled={isId}
                          className="flex-1 border-0 border-b-2 border-gray-200 focus:border-pink-400 bg-transparent px-0 py-1 text-sm font-medium outline-none disabled:text-gray-500"
                        />
                        {!isId && (
                          <select
                            value={field.type}
                            onChange={(e) => updateField(i, { type: e.target.value, values: [] })}
                            title={t("formSettings.colType")}
                            className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 focus:border-pink-400 outline-none bg-white"
                          >
                            {FORM_TYPES.filter((ft) => ft !== "title").map((ft) => (
                              <option key={ft} value={ft}>
                                {t(`formSettings.type.${ft}`)}
                              </option>
                            ))}
                          </select>
                        )}
                        {isId && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <i className="fa-solid fa-lock text-xs" aria-hidden="true" />
                            {t("wizard.formIdLocked")}
                          </span>
                        )}
                      </div>

                      <input
                        type="text"
                        value={field.description}
                        onChange={(e) => updateField(i, { description: e.target.value })}
                        placeholder={t("formSettings.descriptionPlaceholder")}
                        className="w-full text-xs text-gray-500 border-0 border-b border-gray-100 focus:border-pink-300 bg-transparent px-0 py-0.5 outline-none"
                      />

                      {isChoice && (
                        <div className="mt-3 space-y-1.5">
                          {field.values.map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                              <span className="text-gray-400 text-xs w-4 shrink-0">
                                {field.type === "checkbox" ? (
                                  <i className="fa-regular fa-square" aria-hidden="true" />
                                ) : (
                                  <i className="fa-regular fa-circle" aria-hidden="true" />
                                )}
                              </span>
                              <input
                                type="text"
                                value={opt}
                                onChange={(e) => updateOption(i, oi, e.target.value)}
                                placeholder={`Option ${oi + 1}`}
                                className="flex-1 text-sm border-0 border-b border-gray-200 focus:border-pink-300 bg-transparent px-0 py-0.5 outline-none"
                              />
                              <button
                                type="button"
                                onClick={() => removeOption(i, oi)}
                                className="text-gray-300 hover:text-red-400 transition-colors"
                                aria-label={t("wizard.formRemoveOption")}
                              >
                                <i className="fa-solid fa-xmark text-xs" aria-hidden="true" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addOption(i)}
                            className="flex items-center gap-1.5 text-xs text-pink-600 hover:text-pink-700 mt-1"
                          >
                            <i className="fa-solid fa-plus text-xs" aria-hidden="true" />
                            {t("wizard.formAddOption")}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {!isFirstCard && !isId && (
                  <button
                    type="button"
                    onClick={() => removeField(i)}
                    className="shrink-0 p-1.5 text-gray-300 hover:text-red-400 transition-colors"
                    aria-label="Remove field"
                  >
                    <i className="fa-solid fa-trash-can text-sm" aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add field */}
      <div className="flex flex-wrap gap-2">
        {FORM_TYPES.filter((ft) => ft !== "title").map((ft) => (
          <button
            key={ft}
            type="button"
            onClick={() => addField(ft)}
            className="inline-flex items-center gap-1.5 text-xs border border-dashed border-gray-300 hover:border-pink-400 hover:text-pink-600 rounded-lg px-3 py-1.5 text-gray-500 transition-colors"
          >
            <i className="fa-solid fa-plus text-xs" aria-hidden="true" />
            {t(`formSettings.type.${ft}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Loading Screen ────────────────────────────────────────────────────────

function LoadingScreen({ status }: { status: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-6 min-h-[300px]">
      <div className="relative">
        <img
          src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`}
          srcSet={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png 1x, ${process.env.PUBLIC_URL || ""}/assets/logo_256x256.png 2x, ${process.env.PUBLIC_URL || ""}/assets/logo_512x512.png 4x`}
          alt="MeW"
          className="w-20 h-20 opacity-90"
        />
        <span className="absolute inset-0 -m-2.5 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin" />
      </div>
      <p className="text-gray-600 text-sm">{status}</p>
    </div>
  );
}

// ─── Done Screen ───────────────────────────────────────────────────────────

function DoneStep({
  ids,
  onFinish,
  t,
}: {
  ids: BackendIds;
  onFinish: () => void;
  t: ReturnType<typeof useT>;
}) {
  const [sheetId, setSheetId] = useState(ids.responsesSheetId);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  async function createSheet() {
    setCreateError("");
    setCreating(true);
    try {
      const r = await createDetachedResponsesSheet(ids.dataSpreadsheetId);
      setSheetId(r.responsesSheetId);
    } catch (e) {
      setCreateError((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="text-center">
        <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
          <i className="fa-solid fa-check text-xl" aria-hidden="true" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{t("wizard.doneTitle")}</h2>
        <p className="mt-2 text-gray-600">{t("wizard.doneIntro")}</p>
      </div>

      {!sheetId && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 space-y-3">
          <p className="flex items-start gap-2">
            <i className="fa-solid fa-triangle-exclamation mt-0.5" aria-hidden="true" />
            <span>{t("wizard.noSheetAttached")}</span>
          </p>
          {createError && <p className="text-red-600 text-xs">{createError}</p>}
          <button
            type="button"
            onClick={createSheet}
            disabled={creating}
            className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm hover:bg-amber-100 disabled:opacity-50"
          >
            <i className="fa-solid fa-table text-amber-600" aria-hidden="true" />
            {creating ? t("wizard.creatingSheet") : t("wizard.createSheetOptional")}
          </button>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 p-4 sm:p-5">
        <h3 className="text-base font-semibold text-gray-900 mb-3">{t("emailBackend.title")}</h3>
        <EmailBackendInstructions
          dataSpreadsheetId={ids.dataSpreadsheetId}
          formId={ids.formId}
          responsesSheetId={sheetId}
        />
      </div>

      <button
        type="button"
        onClick={onFinish}
        className="w-full inline-flex items-center justify-center gap-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors"
      >
        <i className="fa-solid fa-check text-xs" aria-hidden="true" />
        {t("wizard.doneFinish")}
      </button>
    </div>
  );
}

// ─── Main Wizard ───────────────────────────────────────────────────────────

export default function SetupWizard({ onComplete }: Props) {
  const t = useT();
  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState<EmailSettings>({ ...DEFAULT_EMAIL_SETTINGS });
  const [campusRows, setCampusRows] = useState<CampusRow[]>(
    DEFAULT_CAMPUS_OPTIONS.map((o) => ({ label: o.label, value: o.value }))
  );
  const [fields, setFields] = useState<FormField[]>(() => cloneFields(DEFAULT_FORM_FIELDS));
  const [formMode, setFormMode] = useState<"create" | "reuse">("create");
  const [reuseForm, setReuseForm] = useState<{ id: string; name: string } | null>(null);
  const [templateForm, setTemplateForm] = useState<{ id: string; name: string } | null>(null);
  const [loadingStatus, setLoadingStatus] = useState("");
  const [error, setError] = useState("");
  const [backendIds, setBackendIds] = useState<BackendIds | null>(null);

  const totalSteps = 4;

  function validateForm(): string | null {
    if (fields.length === 0) return t("formSettings.noFields");
    if (fields[0]?.type !== "title") return t("formSettings.titleFirstRequired");
    const questionFields = fields.filter((f) => f.type !== "title");
    if (questionFields.length === 0) return t("formSettings.noQuestions");
    if (findIdFieldIndex(fields) === -1)
      return t("formSettings.idFieldMissing", { label: ID_LABEL });
    return null;
  }

  async function pickReuseForm() {
    setError("");
    try {
      const picked = await pickGoogleForm(t("wizard.reusePickTitle"));
      if (picked) setReuseForm(picked);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function pickTemplateForm() {
    setError("");
    try {
      const picked = await pickGoogleForm(t("wizard.templatePickTitle"));
      if (picked) setTemplateForm(picked);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function runSetup() {
    if (formMode === "create") {
      const formError = validateForm();
      if (formError) { setError(formError); return; }
    } else if (!reuseForm) {
      setError(t("wizard.reuseNeedSelect"));
      return;
    }
    setError("");
    setStep("loading");
    try {
      setLoadingStatus(t("wizard.creatingProject"));
      // This account is creating/owning the project — clear any stale joined-project flags
      // left in this browser by a previous "Join a shared project", so the owner resolves
      // resources via create/search rather than being prompted to Pick shared subfolders.
      setSharedProject(false);
      setFieldworkerMode(false);
      const { id: sid } = await ensureSpreadsheetChecked();
      await ensureAllSheets(sid);
      setLoadingStatus(t("wizard.savingSettings"));
      await writeEmailSettings(sid, email);
      await writeCampusOptions(sid, campusRows);
      await writeFormFields(sid, fields);
      if (formMode === "reuse" && reuseForm) {
        // Reuse the existing form by ID, read-only. ensureFormOnStart then skips
        // creation and publishing (the form is locked) and only ensures a responses sheet.
        setLoadingStatus(t("wizard.linkingForm"));
        await linkExistingFormById(sid, reuseForm.id);
      } else {
        // Create mode: optionally adopt a themed template the new form is copied from
        // (its background/theme carry over; questions still come from the schema).
        if (templateForm) {
          setLoadingStatus(t("wizard.applyingTemplate"));
          await setThemedTemplateFromPicked(templateForm.id);
        }
        setLoadingStatus(t("wizard.creatingForm"));
      }
      const r = await ensureFormOnStart(sid);
      // Show the backend-setup step: the user installs the mewmail library + runs setup,
      // which attaches the form to the responses sheet and enables email notifications.
      setBackendIds({ dataSpreadsheetId: sid, formId: r.formId, responsesSheetId: r.responsesSheetId });
      setStep("done");
    } catch (e) {
      setError(t("wizard.setupError", { msg: (e as Error).message }));
      setStep(4);
    }
  }

  function next() {
    if (step === 1) setStep(2);
    else if (step === 2) setStep(3);
    else if (step === 3) setStep(4);
  }
  function back() {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
    else if (step === 4) setStep(3);
  }

  const isLoading = step === "loading";
  const isDone = step === "done";
  const currentNum = typeof step === "number" ? step : 4;

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
      <div className="min-h-full flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`}
              srcSet={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png 1x, ${process.env.PUBLIC_URL || ""}/assets/logo_256x256.png 2x`}
              alt="MeW"
              className="w-12 h-12"
            />
          </div>

          {/* Step indicator (hidden during loading and on the done screen) */}
          {!isLoading && !isDone && (
            <StepIndicator step={step} t={t} />
          )}

          {/* Card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-6 sm:p-8">
            {isLoading && <LoadingScreen status={loadingStatus} />}

            {isDone && backendIds && (
              <DoneStep ids={backendIds} onFinish={() => onComplete()} t={t} />
            )}

            {!isLoading && !isDone && (
              <>
                {step === 1 && <WelcomeStep t={t} />}
                {step === 2 && (
                  <EmailStep
                    email={email}
                    onChange={(k, v) => setEmail((s) => ({ ...s, [k]: v }))}
                    t={t}
                  />
                )}
                {step === 3 && (
                  <CampusStep rows={campusRows} onChange={setCampusRows} t={t} />
                )}
                {step === 4 && (
                  <div className="space-y-5">
                    {/* Choose how to provide the Google Form */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormMode("create")}
                        className={`text-left rounded-xl border p-4 transition-colors ${
                          formMode === "create"
                            ? "border-pink-400 bg-pink-50/40 ring-1 ring-pink-300"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center gap-2 font-medium text-gray-900">
                          <i className="fa-solid fa-wand-magic-sparkles text-pink-500" aria-hidden="true" />
                          {t("wizard.formModeCreate")}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">{t("wizard.formModeCreateHelp")}</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormMode("reuse")}
                        className={`text-left rounded-xl border p-4 transition-colors ${
                          formMode === "reuse"
                            ? "border-pink-400 bg-pink-50/40 ring-1 ring-pink-300"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center gap-2 font-medium text-gray-900">
                          <i className="fa-solid fa-arrows-rotate text-pink-500" aria-hidden="true" />
                          {t("wizard.formModeReuse")}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">{t("wizard.formModeReuseHelp")}</p>
                      </button>
                    </div>

                    <PickerCookieHint />

                    {formMode === "create" ? (
                      <>
                        <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                            <i className="fa-solid fa-palette text-pink-500" aria-hidden="true" />
                            {t("wizard.templateSectionTitle")}
                          </div>
                          <p className="text-xs text-gray-500">{t("wizard.templateHelp")}</p>
                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={pickTemplateForm}
                              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
                            >
                              <i className="fa-solid fa-folder-open text-gray-500" aria-hidden="true" />
                              {templateForm ? t("wizard.templateChangeButton") : t("wizard.templateUseButton")}
                            </button>
                            {templateForm && (
                              <span className="inline-flex items-center gap-1.5 text-sm text-gray-700 break-all">
                                <i className="fa-solid fa-clipboard-list text-pink-500" aria-hidden="true" />
                                <span className="font-medium">{templateForm.name}</span>
                                <button
                                  type="button"
                                  onClick={() => setTemplateForm(null)}
                                  className="text-gray-400 hover:text-red-500"
                                  title={t("wizard.templateClear")}
                                  aria-label={t("wizard.templateClear")}
                                >
                                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                                </button>
                              </span>
                            )}
                          </div>
                        </div>
                        <FormStep fields={fields} onChange={setFields} t={t} />
                      </>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <h2 className="text-xl font-bold text-gray-900">{t("wizard.reuseTitle")}</h2>
                          <p className="mt-1 text-sm text-gray-600">{t("wizard.reuseIntro")}</p>
                        </div>
                        <button
                          type="button"
                          onClick={pickReuseForm}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50"
                        >
                          <i className="fa-solid fa-folder-open text-gray-500" aria-hidden="true" />
                          {reuseForm ? t("wizard.reuseChangeButton") : t("wizard.reuseSelectButton")}
                        </button>
                        {reuseForm && (
                          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 flex items-center gap-2 break-all">
                            <i className="fa-solid fa-clipboard-list text-pink-500" aria-hidden="true" />
                            <span className="font-medium">{reuseForm.name}</span>
                          </div>
                        )}
                        <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800 flex items-start gap-2">
                          <i className="fa-solid fa-lock mt-0.5" aria-hidden="true" />
                          <span>{t("wizard.reuseReadOnlyNote")}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {error && (
                  <p className="mt-4 text-sm text-red-600 flex items-center gap-2">
                    <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                    {error}
                  </p>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8 pt-4 border-t border-gray-100">
                  <div>
                    {step > 1 && (
                      <button
                        type="button"
                        onClick={back}
                        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        <i className="fa-solid fa-chevron-left text-xs" aria-hidden="true" />
                        {t("wizard.back")}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">
                      {t("wizard.stepOf", {
                        current: String(currentNum),
                        total: String(totalSteps),
                      })}
                    </span>
                    {step < 4 ? (
                      <button
                        type="button"
                        onClick={next}
                        className="inline-flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg px-5 py-2 text-sm font-medium transition-colors"
                      >
                        {t("wizard.next")}
                        <i className="fa-solid fa-chevron-right text-xs" aria-hidden="true" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={runSetup}
                        className="inline-flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg px-5 py-2 text-sm font-medium transition-colors"
                      >
                        <i className="fa-solid fa-rocket text-xs" aria-hidden="true" />
                        {t("wizard.finish")}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
